import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import i18n from 'i18n-js';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { DnDtable } from 'components/UI';
import EditIcon from '@mui/icons-material/Edit';
import { parseRulesHours } from 'services/rules';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/AddCircle';
import { defaultBusinessHoursData } from 'services/rules';
import { BusinessHoursRulesModal } from 'components/Modals';

const BusinessHoursTable = ({ rules, action, bh = [], onDelete, onSubmitHours }) => {
  const [businessHours, setBusinessHours] = useState(null);
  const [openBusinessHoursRulesModal, setOpenBusinessHoursRulesModal] = useState(false);
  const [columns, setColumns] = useState([
    {
      name: '',
      style: { width: '0%' },
    },
    {
      name: i18n.t('business_hours.day'),
      selector: 'day',
      style: { width: '40%', textTransform: 'capitalize' },
      cell: (row) => (
        <Typography>
          <Box component="span" fontSize={12}>
            {i18n.t(`common.${row.day}`)}
          </Box>
        </Typography>
      ),
    },
    {
      name: `${i18n.t('business_hours.from')} - ${i18n.t('business_hours.to')}`,
      selector: 'from',
      style: { width: '40%' },
      cell: (row) => (
        <Box display="flex" flexDirection="column">
          {row?.closed ? (
            <Typography>
              <Box component="span" fontSize={12}>
                {i18n.t('business_hours.closed')}
              </Box>
            </Typography>
          ) : row?.fullDay ? (
            <Typography>
              <Box component="span" fontSize={12}>
                {i18n.t('business_hours.open')}
              </Box>
            </Typography>
          ) : (
            <>
              {row.hours.map((hours, i) => (
                <Typography key={i}>
                  <Box component="span" fontSize={12}>
                    {parseRulesHours(hours.start)} - {parseRulesHours(hours.end)}
                  </Box>
                </Typography>
              ))}
            </>
          )}
        </Box>
      ),
    },
  ]);

  /**
   * Handle open/close rules modal
   */
  const handleOpenBusinessHoursRulesModal = () => {
    setOpenBusinessHoursRulesModal(!openBusinessHoursRulesModal);
  };

  useEffect(() => {
    if (bh.length > 0) {
      setBusinessHours(defaultBusinessHoursData(bh));
    }
  }, [bh]);

  useEffect(() => {
    if (action !== 'details') {
      setColumns((prevValue) => [
        ...prevValue,
        {
          name: i18n.t('common.actions'),
          align: 'center',
          selector: 'action',
          cell: (row) => {
            return (
              <Box display="flex" justifyContent="center">
                <IconButton
                  aria-label="edit"
                  size="medium"
                  onClick={() => {
                    handleOpenBusinessHoursRulesModal();
                  }}
                >
                  <EditIcon fontSize="inherit" />
                </IconButton>
                <IconButton
                  size="medium"
                  aria-label="delete"
                  disabled={row.closed}
                  onClick={() => onDelete(row)}
                >
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </Box>
            );
          },
        },
      ]);
    }
  }, [action]); // eslint-disable-line

  const handleBHSubmit = (bh) => {
    onSubmitHours(bh);
  };

  return (
    <Grid size={12}>
      <Paper elevation={1} sx={{ boxShadow: '0px 3px 6px #0000001A' }}>
        <DnDtable columns={columns} editable={false} data={businessHours} />
        {action !== 'details' && (
          <>
            <Divider />
            <Grid size={12}>
              <Grid
                container
                spacing={1}
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
              >
                <Grid>
                  <Box display="flex" justifyContent="center" fontSize={13}>
                    <Button
                      data-cy="add-rule-button"
                      color="primary"
                      style={{
                        textTransform: 'capitalize',
                        marginRight: 15,
                      }}
                      onClick={() => {
                        handleOpenBusinessHoursRulesModal();
                      }}
                      startIcon={<AddIcon />}
                    >
                      {i18n.t('ruleset.add_rule')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
      </Paper>
      {openBusinessHoursRulesModal && (
        <BusinessHoursRulesModal
          rules={rules}
          onSubmitHours={handleBHSubmit}
          open={openBusinessHoursRulesModal}
          handleClick={handleOpenBusinessHoursRulesModal}
        />
      )}
    </Grid>
  );
};

BusinessHoursTable.propTypes = {
  rules: PropTypes.array,
  action: PropTypes.string,
  bh: PropTypes.array,
  onDelete: PropTypes.func,
  onSubmitHours: PropTypes.func,
};

export default BusinessHoursTable;
